/**
 * Sanitized HTML with search hits wrapped in real `<mark>` elements.
 * Matching follows `splitHighlight`: case- and accent-insensitive by default,
 * at most ten terms (`maxTerms`); the query may be the terms of a text query
 * (`TextQueryTerm[]`) and `fold: 'spelled'` marks exactly what the search
 * found. A hit may run through inline formatting (`<b>`, `<a>`, `<span>`) and
 * stops at a block or a line break — so does a word (`startsWith` / `endsWith`)
 * and a phrase. Needs a DOM (browser or jsdom).
 */
import type { TextQueryTerm } from '../lib/textQuery';
import { sanitizeHtml, type NxSanitizeOptions } from '../Markdown';
import { splitHighlight, type NxHighlightOptions, type NxHighlightQuery } from './highlight';

export interface NxHighlightHtmlOptions extends NxHighlightOptions {
  /** Forwarded to `sanitizeHtml` before matching. */
  sanitize?: NxSanitizeOptions;
}

const MARK_CLASS = 'nx-highlight__mark';

/**
 * Block and line-break elements end an inline run, so a hit never crosses them.
 * The list is the HTML block set plus the breaks named in the kit request.
 */
const BLOCK_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'br',
  'caption',
  'center',
  'dd',
  'details',
  'dialog',
  'dir',
  'div',
  'dl',
  'dt',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hgroup',
  'hr',
  'legend',
  'li',
  'main',
  'menu',
  'nav',
  'ol',
  'optgroup',
  'option',
  'p',
  'pre',
  'search',
  'section',
  'select',
  'summary',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
]);

/** Text here is never marked, even when a sanitizer left the element in place. */
const SKIPPED_TAGS = new Set(['noscript', 'script', 'style', 'textarea', 'title']);

/** An author `<mark>` stays as written, and a second pass must not nest hits. */
const MARK_TAGS = new Set(['mark']);

interface Slice {
  node: Text;
  start: number;
  end: number;
}

function tagName(node: Element): string {
  return node.tagName.toLowerCase();
}

function hasAncestor(node: Text, tags: Set<string>): boolean {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    if (tags.has(tagName(parent))) return true;
  }
  return false;
}

/** Consecutive text nodes, split wherever a block or skipped element intervenes. */
function inlineRuns(root: ParentNode): Text[][] {
  const runs: Text[][] = [];
  let current: Text[] = [];

  function flush(): void {
    if (current.length === 0) return;
    runs.push(current);
    current = [];
  }

  function walk(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node as Text;
      if (text.length === 0 || hasAncestor(text, SKIPPED_TAGS)) return;
      current.push(text);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const tag = tagName(node as Element);
    if (SKIPPED_TAGS.has(tag)) {
      flush();
      return;
    }
    if (BLOCK_TAGS.has(tag)) {
      flush();
      for (const child of node.childNodes) walk(child);
      flush();
      return;
    }
    for (const child of node.childNodes) walk(child);
  }

  for (const child of root.childNodes) walk(child);
  flush();
  return runs;
}

function hitSlices(nodes: Text[], query: NxHighlightQuery, options: NxHighlightOptions): Slice[] {
  let text = '';
  const spans: { node: Text; start: number; end: number }[] = [];
  for (const node of nodes) {
    const start = text.length;
    text += node.data;
    spans.push({ node, start, end: text.length });
  }
  if (!text) return [];

  const segments = splitHighlight(text, query, options);
  const slices: Slice[] = [];
  let at = 0;
  for (const segment of segments) {
    const next = at + segment.text.length;
    if (segment.hit) {
      for (const span of spans) {
        const from = Math.max(at, span.start);
        const to = Math.min(next, span.end);
        if (from < to && !hasAncestor(span.node, MARK_TAGS)) {
          slices.push({ node: span.node, start: from - span.start, end: to - span.start });
        }
      }
    }
    at = next;
  }
  return slices;
}

function laterFirst(a: Slice, b: Slice): number {
  if (a.node !== b.node) {
    const position = a.node.compareDocumentPosition(b.node);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return 1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return -1;
  }
  return b.start - a.start;
}

function wrapSlice(slice: Slice): void {
  const { node, start, end } = slice;
  const parent = node.parentNode;
  if (!parent || start >= end || start < 0 || end > node.length) return;
  let target = node;
  if (end < node.length) node.splitText(end);
  if (start > 0) target = node.splitText(start);
  const mark = node.ownerDocument.createElement('mark');
  mark.className = MARK_CLASS;
  parent.insertBefore(mark, target);
  mark.append(target);
}

function markRun(nodes: Text[], query: NxHighlightQuery, options: NxHighlightOptions): boolean {
  const slices = hitSlices(nodes, query, options);
  if (slices.length === 0) return false;
  slices.sort(laterFirst);
  for (const slice of slices) wrapSlice(slice);
  return true;
}

/**
 * Sanitizes `html`, then wraps matches of `query` (a string, a list of strings
 * or `TextQueryTerm`s) in `<mark class="nx-highlight__mark">`. Without a query
 * (or with no term left after the length and term limits) the result is the
 * sanitized HTML alone.
 */
export function highlightHtml(
  html: string | null | undefined,
  query: string | readonly string[] | readonly TextQueryTerm[] | null | undefined = '',
  options: NxHighlightHtmlOptions = {}
): string {
  const clean = sanitizeHtml(html ?? '', options.sanitize ?? {});
  if (!clean) return '';

  const doc = new DOMParser().parseFromString(clean, 'text/html');
  const matchOptions: NxHighlightOptions = {
    match: options.match,
    minLength: options.minLength,
    caseSensitive: options.caseSensitive,
    ignoreDiacritics: options.ignoreDiacritics,
    maxTerms: options.maxTerms,
    fold: options.fold,
  };
  let marked = false;
  for (const run of inlineRuns(doc.body)) {
    if (markRun(run, query ?? '', matchOptions)) marked = true;
  }
  return marked ? doc.body.innerHTML : clean;
}
